// Region locations grid: reorder cards by distance to a geocoded user query.
// Distance sorting uses the shared FBGeo.reorder (assets/geo-utils.js) after a
// server-side geocode resolves the query to lat/lng. Falls back silently to the
// server-rendered order on any error. All user-facing copy is read from data-*
// attributes on the search form (translated in the section markup).
//
// R-GEO-BLOCKER — LAUNCH STATE:
// Geocoding is disabled by the hard kill switch below. Browser-direct Nominatim
// cannot send the OSM-required identification header (User-Agent is forbidden to
// fetch; Referer is browser-controlled), so it is a release blocker. Until a
// server-side app-proxy (or keyed provider) exists, GEOCODE_ENDPOINT stays null
// and the search submit is a no-op — no network request, no error/no-result copy.
// Re-enabling requires this code push (the `show_search` theme setting is NOT the
// gate); a bad theme-editor click can never turn geocoding on.
//
// DEV-ONLY TEST HOOK: to exercise the search flow on a dev theme, a dev may
// temporarily set GEOCODE_ENDPOINT to a test proxy URL, e.g.
//   const GEOCODE_ENDPOINT = 'https://<app-proxy-host>/geocode';
// The endpoint must accept `?format=json&limit=1&q=<query>` and return a JSON
// array of { lat, lon, display_name }. Never commit a non-null value.

const GEOCODE_ENDPOINT = null; // R-GEO-BLOCKER: set to the app-proxy URL when server-side geocoding lands

const FETCH_TIMEOUT_MS = 6000;

// Geocodes `query` via the configured endpoint. `controller` is supplied by the
// caller so both the timeout and the race-guard can abort the same request.
//
// DATA-EXPOSURE INVENTORY (privacy-policy pointer): this request transmits the
// visitor's typed query (personal location data) AND their IP address to the
// endpoint's operator — OpenStreetMap Foundation (OSMF) when Nominatim is the
// backing provider. It is strictly user-initiated: fired only from the form's
// explicit `submit` handler below, never on page load, focus, or keystroke.
async function geocode(query, controller) {
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const url = `${GEOCODE_ENDPOINT}?format=json&limit=1&q=${encodeURIComponent(query)}`;
    const res = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
    if (!res.ok) throw new Error('geocode failed');
    const arr = await res.json();
    if (!arr.length) return null;
    return {
      lat: parseFloat(arr[0].lat),
      lng: parseFloat(arr[0].lon),
      label: arr[0].display_name,
    };
  } finally {
    clearTimeout(timer);
  }
}

function setStatus(el, message) {
  if (!el) return;
  if (!message) {
    el.hidden = true;
    el.textContent = '';
    return;
  }
  el.hidden = false;
  el.textContent = message;
}

function init() {
  const form = document.querySelector('[data-region-search]');
  const grid = document.querySelector('[data-search-grid]');
  if (!form || !grid) return;
  const status = document.querySelector('[data-search-status]');
  const input = form.querySelector('input[type="search"]');
  const submitBtn = form.querySelector('[type="submit"]');

  // All status copy comes from data-* attributes emitted (and translated) by the
  // section markup — zero hardcoded strings here.
  const msg = {
    searching: form.dataset.msgSearching || '',
    noResult: form.dataset.msgNoResult || '',
    error: form.dataset.msgError || '',
  };

  let requestToken = 0;
  let activeController = null;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    // R-GEO-BLOCKER dark state: no endpoint means no request and no status copy.
    if (!GEOCODE_ENDPOINT) return;

    const query = (input?.value || '').trim();
    if (!query) return;

    // Race guard: abort any in-flight request and invalidate its result, then
    // claim a fresh token so only the latest submit applies its result.
    if (activeController) activeController.abort();
    const controller = new AbortController();
    activeController = controller;
    const token = ++requestToken;

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.setAttribute('aria-busy', 'true');
    }
    setStatus(status, msg.searching);

    try {
      const result = await geocode(query, controller);
      if (token !== requestToken) return; // superseded by a newer submit
      if (!result) {
        setStatus(status, msg.noResult.replace('{query}', query));
        return;
      }
      grid.querySelectorAll(':scope > li[hidden]').forEach((li) => {
        li.hidden = false;
      });
      FBGeo.reorder(grid, result.lat, result.lng);
      setStatus(status, ''); // clear status — the reordered grid is the feedback
    } catch (err) {
      if (token !== requestToken) return; // aborted by a newer submit — stay silent
      console.warn('[region-search]', err);
      setStatus(status, msg.error);
    } finally {
      if (token === requestToken && submitBtn) {
        submitBtn.disabled = false;
        submitBtn.removeAttribute('aria-busy');
      }
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
